import { useEffect, useRef } from 'react';

interface APICall {
  timestamp: number;
  endpoint: string;
  method: string;
  params?: any;
}

// Debug hook to track API calls and detect potential infinite loops
export const useAPICallTracker = (hookName: string) => {
  const callsRef = useRef<APICall[]>([]);
  const warningShownRef = useRef(false);

  const trackCall = (endpoint: string, method: string = 'GET', params?: any) => {
    const now = Date.now();
    const call: APICall = {
      timestamp: now,
      endpoint,
      method,
      params
    };

    callsRef.current.push(call);

    // Keep only calls from last 10 seconds
    callsRef.current = callsRef.current.filter(c => now - c.timestamp < 10000);

    // Check for potential infinite loops
    const recentCalls = callsRef.current.filter(c => 
      c.endpoint === endpoint && 
      now - c.timestamp < 5000 // Last 5 seconds
    );

    if (recentCalls.length > 5 && !warningShownRef.current) {
      console.warn(`🚨 POTENTIAL INFINITE LOOP DETECTED in ${hookName}:`, {
        endpoint,
        callsInLast5Seconds: recentCalls.length,
        recentCalls: recentCalls.map(c => ({
          timestamp: new Date(c.timestamp).toISOString(),
          params: c.params
        }))
      });
      warningShownRef.current = true;

      // Reset warning after 30 seconds
      setTimeout(() => {
        warningShownRef.current = false;
      }, 30000);
    }

    console.log(`📊 ${hookName} API Call:`, {
      endpoint,
      method,
      params,
      totalCallsLast10s: callsRef.current.length
    });
  };

  // Log current state on mount/unmount
  useEffect(() => {
    console.log(`🔌 ${hookName} hook mounted`);
    
    return () => {
      console.log(`🔌 ${hookName} hook unmounted`);
      // Clear calls when component unmounts
      callsRef.current = [];
    };
  }, [hookName]);

  return { trackCall };
};

// Performance monitor for React components
export const useRenderTracker = (componentName: string) => {
  const renderCountRef = useRef(0);
  const lastRenderRef = useRef(Date.now());

  useEffect(() => {
    renderCountRef.current += 1;
    const now = Date.now();
    const timeSinceLastRender = now - lastRenderRef.current;
    lastRenderRef.current = now;

    if (renderCountRef.current > 10 && timeSinceLastRender < 100) {
      console.warn(`🚨 EXCESSIVE RENDERING in ${componentName}:`, {
        renderCount: renderCountRef.current,
        timeSinceLastRender,
        possibleInfiniteLoop: timeSinceLastRender < 50
      });
    }

    console.log(`🎨 ${componentName} render #${renderCountRef.current} (${timeSinceLastRender}ms since last)`);
  });

  return renderCountRef.current;
};
